import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { object, string, number } from 'yup';
import DialogTransition from './Common/DialogTransition';

import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { ReactComponent as DropdownArrow } from '../assets/DropdownArrow.svg';
import useOrganization from '../hooks/UseOrganization';

/**
 * @component CreateEditIDPMapperModal
 * @description CreateEditIDPMapperModal - React component for creating/editing IDP mapping.
 * @param {Object} props - The props object containing the component's data and functions.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.setOpen - Function to set the open state of the dialog.
 * @param {Function} props.createIdpMapping - Function to create a new IDP mapping.
 * @param {Function} props.updateIdpMapping - Function to update an existing IDP mapping.
 * @param {string} props.orgName - The organization ID.
 * @param {Function} props.refreshMappingList - Function to refresh the mapping list.
 * @param {string} props.mode - Mode of the dialog ('create' or 'edit').
 * @param {Object} props.currentMapping - Current mapping data for edit mode.
 * @returns {JSX.Element} React component.
 */

const CreateEditIDPMapperModal = ({
  open,
  setOpen,
  createIdpMapping,
  updateIdpMapping,
  orgName,
  refreshMappingList,
  mode = 'create',
  currentMapping = null,
}) => {
  const theme = useTheme();
  const { getGroupList } = useOrganization();
  const [groupList, setGroupList] = useState([]);

  // Fetch group list when component mounts
  useEffect(() => {
    if (open && orgName) {
      getGroupList(orgName)
        .then((res) => {
          setGroupList(res?.data || []);
        })
        .catch((error) => {
          console.error('Error fetching group list:', error);
        });
    }
  }, [open, orgName]);

  const initialValues = {
    key: '',
    value: '',
    group: {
      label: '',
      id: null,
    },
  };

  /**
   * Validation schema for the form fields.
   * @typedef {Object} ValidationSchema
   * @property {string} key - The key field validation rules.
   * @property {string} value - The value field validation rules.
   * @property {number} group - The group field validation rules.
   */

  const validationSchema = object({
    key: string()
      .required('Required')
      .min(1, 'Key must be at least 1 character')
      .max(255, 'Key must be at most 255 characters'),
    value: string()
      .required('Required')
      .min(1, 'Value must be at least 1 character')
      .max(255, 'Value must be at most 255 characters'),
    group: object().shape({
      label: string().required('Required'),
      id: number().required('Required'),
    }),
  });

  /**
   * Formik form object for managing form state and submission.
   * @typedef {Object} FormikForm
   * @property {Object} initialValues - Initial values for form fields.
   * @property {Object} validationSchema - Validation schema for form fields.
   * @property {Function} onSubmit - Function to handle form submission.
   * @property {Function} handleReset - Function to reset form fields.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (formData) => {
      const payload = {
        data: [
          {
            key: formData.key,
            value: formData.value,
            group: formData.group.id,
          },
        ],
      };

      try {
        if (mode === 'edit' && currentMapping) {
          await updateIdpMapping(orgName, currentMapping.id, {
            key: formData.key,
            value: formData.value,
            group: formData.group.id,
            is_active: true,
          });
        } else {
          await createIdpMapping(orgName, payload);
        }
        refreshMappingList();
      } catch (error) {
        console.error('Error saving IDP mapping:', error);
      }
      setOpen(false);
      formikForm.handleReset();
    },
  });

  // Populate form when in edit mode
  useEffect(() => {
    if (mode === 'edit' && currentMapping) {
      // Find the group from groupList that matches the currentMapping group
      const selectedGroup = groupList.find(
        (group) => group.id === currentMapping.group
      );
      formikForm.setValues({
        key: currentMapping.key || '',
        value: currentMapping.value || '',
        group: selectedGroup
          ? {
              label: selectedGroup.name,
              id: selectedGroup.id,
            }
          : { label: '', id: null },
      });
    } else {
      formikForm.resetForm();
    }
  }, [mode, currentMapping, open, groupList]);

  const handleChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );

  /**
   * @function handleGroupInput
   * @description handles the input field for the group selection in the form.
   * @param {object} params - The parameters for the Autocomplete component.
   * @returns {JSX} - renders The TextField component for Autocomplete
   * @example handleGroupInput(params)
   */
  const handleGroupInput = useCallback(
    (params) => {
      return (
        <TextField
          required
          {...params}
          name="group"
          label="Group"
          fullWidth
          placeholder="Select group"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.group?.id && formikForm?.touched?.group
              ? formikForm.errors.group?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.group?.id) && formikForm?.touched?.group
          }
        />
      );
    },
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    setOpen(false);
    formikForm.handleReset();
  }, [formikForm, setOpen]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>
        {mode === 'edit' ? 'Edit Claim Mapping' : 'Create Claim Mapping'}
      </DialogTitle>
      <IconButton
        size="large"
        onClick={handleOnClose}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent dividers sx={{ padding: '0px' }}>
        <Grid container direction="column" rowSpacing={2} padding={4}>
          <Grid item xs={12} md={8}>
            <TextField
              required
              placeholder="Enter key"
              name="key"
              label="Key"
              value={formikForm.values.key}
              onChange={handleChange}
              aria-label="key"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.key && formikForm?.touched?.key
                  ? formikForm?.errors?.key
                  : null
              }
              error={
                Boolean(formikForm?.errors?.key) && formikForm?.touched?.key
              }
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              required
              label="Value"
              placeholder="Enter value"
              name="value"
              value={formikForm.values.value}
              onChange={handleChange}
              aria-label="value"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.value && formikForm?.touched?.value
                  ? formikForm?.errors?.value
                  : null
              }
              error={
                Boolean(formikForm?.errors?.value) && formikForm?.touched?.value
              }
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              disabled={mode === 'edit'}
              options={
                groupList?.length > 0
                  ? groupList?.map((item) => ({
                      id: item?.id,
                      label: item?.name,
                    }))
                  : []
              }
              popupIcon={<DropdownArrow />}
              noOptionsText="No groups available."
              disableClearable={true}
              getOptionLabel={(item) => (item?.label ? item?.label : '')}
              isOptionEqualToValue={(option, val) =>
                option?.label === val?.label
              }
              value={formikForm.values.group}
              onChange={(_e, newValue) => {
                formikForm.setFieldValue(
                  'group',
                  newValue || { label: '', id: null }
                );
              }}
              renderInput={handleGroupInput}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            formikForm.handleReset();
            const selectedGroup = groupList.find(
              (group) => group.id === currentMapping.group
            );
            if (mode === 'edit') {
              formikForm.setFieldValue(
                'group',
                selectedGroup
                  ? {
                      label: selectedGroup.name,
                      id: selectedGroup.id,
                    }
                  : { label: '', id: null }
              );
            }
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={formikForm.handleSubmit}
          title="Submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditIDPMapperModal;
